import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
} from '@material-ui/core';
import { Save } from '@material-ui/icons';
import { useFormik, getIn } from 'formik';
import { FileUpload } from '../../components/fileUpload/fileUpload';
import { EvaluationModel } from '../../models/evaluationModel';
import { useTranslation } from 'react-i18next';
import { useEvaluationStyles } from './style';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../stores';
import { AuthState } from '../../stores/auth/types';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { getMyTournamentsReq } from '../../api/tournamentCalendar/listApi';
import { TournamentCalendarModel } from '../../models/tournamentCalendarModel';
import { TournamentRoles } from '../../models/eums/tournamentRoles';
import moment from 'moment';
import { TournamentAssigneeModel } from '../../models/tournamentAssigneeModel';
import { AssigneeModel } from '../../models/assigneeModel';
import { validationSchema } from './validation';
import { createEvaluationReq } from '../../api/evaluation/editApi';
import { tournamentCalendarToRefference } from '../../models/tournamentReferenceModel';
import { useHistory } from 'react-router-dom';
import { showSuccess } from '../../services/notification';

export function EvaluationEditor() {
  const evaluationStyles = useEvaluationStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const {
    personFirstName,
    personLastName,
    personId: currentPersonId,
  } = useSelector<ApplicationState, AuthState>(state => state.auth);
  const [myTournaments, setMyTournaments] = useState<TournamentCalendarModel[]>();
  const [selectedTournament, setSelectedTournament] = useState<TournamentCalendarModel>();
  const [tournamentOfficials, setTournamentOfficials] = useState<TournamentAssigneeModel[]>();
  const formikBag = useFormik({
    validationSchema,
    initialValues: {},
    enableReinitialize: true,
    onSubmit: submitEvaluation,
  });
  const {
    values,
    errors,
    touched,
    isValid,
    handleChange,
    setFieldValue,
    submitForm,
    setFieldTouched,
  } = formikBag;

  useEffect(() => {
    (async function getMyTournaments() {
      const myTournamentsResp = await getMyTournamentsReq();
      if (myTournamentsResp && myTournamentsResp.data) setMyTournaments(myTournamentsResp.data);
    })();
  }, []);

  useEffect(() => {
    if (!selectedTournament) return;
    const newTournamentOfficials: TournamentAssigneeModel[] = [];

    const { tournamentDirector } = selectedTournament;
    if (tournamentDirector && tournamentDirector.personUUID.personId !== currentPersonId) {
      newTournamentOfficials.push({
        role: TournamentRoles.SUPERVISOR,
        person: tournamentDirector,
      });
    }

    (selectedTournament.officials || []).forEach(official => {
      if (official.person.personUUID.personId !== currentPersonId) {
        if (
          !newTournamentOfficials.find(
            nto =>
              nto.person.preferredGivenName === official.person.preferredGivenName &&
              nto.person.preferredFamilyName === official.person.preferredFamilyName,
          )
        )
          newTournamentOfficials.push(official);
      }
    });

    setTournamentOfficials(newTournamentOfficials);
  }, [selectedTournament, currentPersonId]);

  function formatTournamentForDisplay(tournament: TournamentCalendarModel) {
    if (!tournament.start || !tournament.name) return '';

    let tournamentStartDate = moment(tournament.start).format('DD.MM.YYYY.');
    return `${tournamentStartDate} ${tournament.name}`;
  }

  function findTournamentById(tournamentId?: string) {
    return myTournaments?.find(tournament => tournament._id === tournamentId) || null;
  }

  function findOfficialByAssignee(assignee?: AssigneeModel) {
    return (
      tournamentOfficials?.find(
        tournamentOfficial =>
          tournamentOfficial.person.personUUID.personId === assignee?.personUUID.personId &&
          tournamentOfficial.person.personUUID.providerId === assignee?.personUUID.providerId,
      ) || null
    );
  }

  function getHelperText(fieldPath: string) {
    if (getIn(touched, fieldPath)) return getIn(errors, fieldPath) || '';

    return '';
  }

  function isFieldError(fieldPath: string) {
    return getIn(touched, fieldPath) && !!getIn(errors, fieldPath);
  }

  async function submitEvaluation(evaluation: EvaluationModel) {
    const createEvaluationResp = await createEvaluationReq(evaluation);
    if (createEvaluationResp.status === 200) {
      showSuccess(t('evaluation_submitted'));
      history.push('/evaluations-evaluator');
    }
  }

  return (
    <Grid item container xs={12} lg={6} direction='column' spacing={3}>
      <Grid item>
        <Typography variant='h5'>{t('evaluation')}</Typography>
      </Grid>

      <Grid item xs={12}>
        <Grid container direction='row'>
          <Typography variant='h6'>{`${t('evaluator')}:`}</Typography>
          <Typography
            style={{ marginLeft: 5 }}
            variant='h6'
          >{`${personFirstName} ${personLastName}`}</Typography>
        </Grid>
      </Grid>

      <Grid item>
        <Autocomplete<TournamentCalendarModel>
          id='select-tournament'
          onBlur={() => setFieldTouched('tournamentId')}
          autoComplete
          includeInputInList
          options={myTournaments || []}
          value={findTournamentById(values.tournament?.tournamentId)}
          getOptionLabel={formatTournamentForDisplay}
          onChange={(ev: any, tournament: TournamentCalendarModel | null) => {
            if (!tournament) return;

            setSelectedTournament(tournament);
            setFieldValue('tournament', tournamentCalendarToRefference(tournament));
            setFieldValue('subject', undefined);
          }}
          renderInput={params => (
            <TextField
              required
              {...params}
              error={isFieldError('tournamentId')}
              label={t('select_tournament')}
              variant='outlined'
              className={evaluationStyles.autocomplete}
              helperText={t(getHelperText('tournamentId'))}
            />
          )}
        />
      </Grid>

      <Grid item>
        <Autocomplete
          id='select-official'
          onBlur={() => setFieldTouched('subject')}
          autoComplete
          includeInputInList
          options={tournamentOfficials || []}
          value={findOfficialByAssignee(values.subject)}
          getOptionLabel={option =>
            `${option.person.preferredGivenName} ${option.person.preferredFamilyName}`
          }
          onChange={(ev: any, tournamentAssignee: TournamentAssigneeModel | null) => {
            if (!tournamentAssignee) return;
            setFieldValue('subject', tournamentAssignee.person);
          }}
          renderInput={params => (
            <TextField
              required
              {...params}
              error={isFieldError('subject')}
              label={t('select_official')}
              variant='outlined'
              className={evaluationStyles.autocomplete}
              helperText={t(getHelperText('subject'))}
            />
          )}
        />
      </Grid>

      <Grid item>
        <FormControl
          required
          variant='standard'
          className={evaluationStyles.formControl}
          error={isFieldError('rating')}
        >
          <InputLabel htmlFor='evaluation-rating'>{t('rating')}</InputLabel>
          <Select
            onBlur={() => setFieldTouched('rating')}
            id='evaluation-rating'
            name='rating'
            value={values.rating || ''}
            onChange={handleChange}
            error={isFieldError('rating')}
          >
            <MenuItem value={1}>1</MenuItem>
            <MenuItem value={2}>2</MenuItem>
            <MenuItem value={3}>3</MenuItem>
            <MenuItem value={4}>4</MenuItem>
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={6}>6</MenuItem>
            <MenuItem value={7}>7</MenuItem>
          </Select>
          <FormHelperText>{t(getHelperText('rating'))}</FormHelperText>
        </FormControl>
      </Grid>

      <Grid item>
        <FileUpload
          filesLimit={1}
          keyValue={1}
          fileUrl={''}
          onChange={templateFile => setFieldValue('file', templateFile)}
        />
        <Typography color='error' variant='body2'>
          {t(errors.file || '')}
        </Typography>
      </Grid>

      <Grid item>
        <Button
          type='submit'
          variant='contained'
          color='primary'
          size='large'
          startIcon={<Save />}
          disabled={!isValid}
          onClick={submitForm}
        >
          {t('save')}
        </Button>
      </Grid>
    </Grid>
  );
}
